import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Wallet, Upload, CheckCircle, Clock, XCircle, AlertCircle, Image, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { CardGuardUpload } from '../components/CardGuardUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DepositPage() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  const [viewImage, setViewImage] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    fetchDeposits();
    fetchBankInfo();
  }, []);

  const fetchBankInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/bank-info`);
      setBankInfo(res.data);
    } catch (error) {
      console.error('Failed to fetch bank info:', error);
    }
  };

  const fetchDeposits = async () => {
    try {
      const response = await api.getDeposits();
      setDeposits(response.data);
    } catch (error) {
      console.error('Failed to fetch deposits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Local preview
    const reader = new FileReader();
    reader.onload = (ev) => setReceiptPreview(ev.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/deposits/upload-receipt`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setReceiptUrl(response.data.file_url);
      toast.success(t('deposit_receipt_uploaded'));
    } catch (error) {
      toast.error(t('deposit_receipt_upload_fail'));
    } finally {
      setUploading(false);
    }
  };

  const clearReceipt = () => {
    setReceiptUrl(null);
    setReceiptPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || parseFloat(amount) <= 0) {
      toast.error(t('deposit_invalid_amount'));
      return;
    }

    try {
      await api.createDeposit({
        amount: parseFloat(amount),
        payment_method: 'bank_transfer',
        receipt_image: receiptUrl,
        notes: notes
      });

      toast.success(t('deposit_submitted'));
      setAmount('');
      setNotes('');
      setReceiptUrl(null);
      setReceiptPreview(null);
      fetchDeposits();
    } catch (error) {
      toast.error(t('deposit_submit_failed'));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: Clock, label: t('pending'), className: 'bg-yellow-500/15 text-yellow-700 border-yellow-500/20' },
      confirmed: { icon: CheckCircle, label: t('confirmed'), className: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/20' },
      rejected: { icon: XCircle, label: t('rejected'), className: 'bg-red-500/15 text-red-700 border-red-500/20' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.className}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl" data-testid="deposit-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            {t('deposit_title')}
          </h1>
          <p className="text-muted-foreground">
            {t('deposit_subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Bank Transfer Form */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-card border border-border/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Wallet className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-semibold text-primary">{t('deposit_bank_title')}</h2>
                  <p className="text-sm text-muted-foreground">{t('deposit_bank_subtitle')}</p>
                </div>
              </div>

              <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900" data-testid="bank-info-display">
                    <p className="font-medium mb-2">{t('deposit_bank_info')}</p>
                    {bankInfo ? (
                      <div className="space-y-1 text-xs">
                        <p>{t('deposit_bank_name')}: {bankInfo.bank_name}</p>
                        <p>{t('deposit_branch_name')}: {bankInfo.branch_name}</p>
                        <p>{t('deposit_account_type')}: {bankInfo.account_type}</p>
                        <p>{t('deposit_account_number')}: {bankInfo.account_number}</p>
                        <p>{t('deposit_account_holder')}: {bankInfo.account_holder}</p>
                        {bankInfo.notes && <p className="mt-1 text-muted-foreground">{bankInfo.notes}</p>}
                      </div>
                    ) : (
                      <p className="text-xs">{t('loading')}</p>
                    )}
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="deposit-form">
                <div className="space-y-2">
                  <Label htmlFor="amount">{t('deposit_amount')}</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                      ¥
                    </span>
                    <Input
                      id="amount"
                      type="number"
                      placeholder="10,000"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="pl-8"
                      min="1000"
                      required
                      data-testid="amount-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="receipt">{t('deposit_receipt_label')}</Label>
                  <CardGuardUpload
                    label={t('deposit_receipt_label')}
                    uploading={uploading}
                    previewUrl={receiptPreview}
                    onUpload={(file) => {
                      const e = { target: { files: [file] } };
                      handleFileUpload(e);
                    }}
                    onRemove={clearReceipt}
                    testId="receipt-upload"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">{t('note')}</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder={t('deposit_reference_placeholder')}
                    data-testid="notes-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-full"
                  data-testid="submit-deposit-button"
                >
                  {t('deposit_submit')}
                </Button>
              </form>
            </div>
          </div>

          {/* Deposit History */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border/50 rounded-2xl p-6">
              <h2 className="font-heading text-lg font-semibold text-primary mb-6">{t('deposit_history')}</h2>
              {loading ? (
                <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
              ) : deposits.length === 0 ? (
                <div className="text-center py-12">
                  <Wallet className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">{t('deposit_no_history')}</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="deposit-list">
                  {deposits.map((deposit) => (
                    <div
                      key={deposit.id}
                      className="flex items-center justify-between p-4 border border-border rounded-xl hover:bg-secondary/50 smooth-transition"
                      data-testid={`deposit-item-${deposit.id}`}
                    >
                      <div className="flex items-center gap-4">
                        {deposit.receipt_image ? (
                          <div
                            className="w-10 h-10 rounded-lg overflow-hidden border border-border cursor-pointer flex-shrink-0"
                            onClick={() => setViewImage(`${API_URL}${deposit.receipt_image}`)}
                            data-testid={`deposit-receipt-thumb-${deposit.id}`}
                          >
                            <img
                              src={`${API_URL}${deposit.receipt_image}`}
                              alt={t('deposit_receipt_alt')}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <Wallet className="h-5 w-5 text-accent" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-primary font-mono">¥{deposit.amount.toLocaleString()}</div>
                          <div className="text-sm text-muted-foreground">
                            {new Date(deposit.created_at).toLocaleString('ja-JP')}
                          </div>
                          {deposit.notes && (
                            <div className="text-xs text-muted-foreground mt-1">{deposit.notes}</div>
                          )}
                          {deposit.receipt_image && (
                            <button
                              className="text-xs text-blue-600 hover:underline mt-0.5 flex items-center gap-1"
                              onClick={() => setViewImage(`${API_URL}${deposit.receipt_image}`)}
                            >
                              <Image className="h-3 w-3" />
                              {t('deposit_receipt_alt')}
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        {getStatusBadge(deposit.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Viewer Dialog */}
      <Dialog open={!!viewImage} onOpenChange={() => setViewImage(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('deposit_receipt_preview_alt')}</DialogTitle>
          </DialogHeader>
          {viewImage && (
            <div className="mt-2">
              <img
                src={viewImage}
                alt={t('deposit_receipt_preview_alt')}
                className="w-full rounded-lg"
                data-testid="receipt-viewer-image"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
